import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Activity, Footprints, Car, CalendarDays, Users, Baby, MapPin } from "lucide-react";
import { api, photoUrl, priceLabel } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useBooking } from "../context/BookingContext";
import { Reveal } from "../components/Reveal";
import { diffLabel, durLabel, transportLabel, distLabel } from "../lib/labels";
import LegalBadges from "../components/LegalBadges";
import AltaiMap from "../components/AltaiMap";
import Seo from "../components/Seo";
import { excursionLd, breadcrumbLd } from "../lib/seo";

const seasonLabels = { spring: { ru: "Весна", en: "Spring" }, summer: { ru: "Лето", en: "Summer" }, autumn: { ru: "Осень", en: "Autumn" }, winter: { ru: "Зима", en: "Winter" } };

export default function ExcursionDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { openBooking } = useBooking();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/excursions/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: "#11110F" }}>
      <p className="text-argali-ivory/70">{lang === "ru" ? "Маршрут не найден" : "Route not found"}</p>
      <Link to="/excursions" className="btn-outline mt-6">{t("nav_excursions")}</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen" style={{ background: "#11110F" }} />;

  const { excursion: e, route_places: places } = data;
  const title = lang === "ru" ? e.title_ru : e.title_en;
  const desc = lang === "ru" ? e.short_description_ru : e.short_description_en;
  const seasons = (e.seasons || []).map((s) => seasonLabels[s][lang]).join(" · ");
  const routeCoords = places.map((p) => [p.lat, p.lng]);
  const geoFeatures = places.map((p) => ({
    type: "Feature", geometry: { type: "Point", coordinates: [p.lng, p.lat] },
    properties: { id: p.id, name_ru: p.name_ru, name_en: p.name_en, district: p.district, category: p.category, photo_id: p.photo_id, excursions: [] },
  }));

  const info = [
    { icon: Clock, label: t("duration"), value: durLabel(e.duration, lang) },
    { icon: Activity, label: t("difficulty"), value: diffLabel(e.difficulty, lang) },
    { icon: Footprints, label: t("walking"), value: e.walking_km_estimate ? `${e.walking_km_estimate} ${lang === "ru" ? "км" : "km"}` : "—" },
    { icon: Car, label: t("transport"), value: transportLabel(e.transport, lang) },
    { icon: CalendarDays, label: t("season"), value: seasons },
    { icon: Users, label: lang === "ru" ? "Формат" : "Format", value: (e.booking_type || []).join(", ").replace(/_/g, " ") },
  ];

  return (
    <div data-testid="excursion-detail" style={{ background: "#F4F0E7" }}>
      <Seo
        path={`/excursions/${e.slug}`}
        lang={lang}
        title={(lang === "ru" ? e.seo?.title_ru : e.seo?.title_en) || title}
        description={lang === "ru" ? e.seo?.meta_description_ru : e.seo?.meta_description_en}
        image={photoUrl(e.hero_photo_id)}
        type="article"
        jsonLd={[
          excursionLd(e, lang, photoUrl(e.hero_photo_id)),
          breadcrumbLd([
            { name: "АРГАЛИ", path: "/" },
            { name: lang === "ru" ? "Экскурсии" : "Excursions", path: "/excursions" },
            { name: title, path: `/excursions/${e.slug}` },
          ]),
        ]}
      />
      {/* HERO */}
      <section className="relative grain overflow-hidden" style={{ height: "100svh" }}>
        <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 2 }} src={photoUrl(e.hero_photo_id)} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(17,17,15,0.5), rgba(17,17,15,0.2) 40%, rgba(17,17,15,0.85))" }} />
        <div className="relative h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }} className="eyebrow mb-4">
            АРГАЛИ · {lang === "ru" ? "Маршрут" : "Journey"}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9 }} className="display display-lg text-argali-ivory max-w-4xl">
            {title}
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }} className="flex flex-wrap items-center gap-4 mt-6 text-[0.7rem] uppercase tracking-[0.14em] text-argali-ivory/80">
            <span>{durLabel(e.duration, lang)}</span><span className="w-1 h-1 rounded-full bg-argali-sandstone/60" />
            <span>{diffLabel(e.difficulty, lang)}</span><span className="w-1 h-1 rounded-full bg-argali-sandstone/60" />
            <span>{seasons}</span>
          </motion.div>
          <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
            onClick={() => openBooking(e)} data-testid="detail-request-hero" className="btn-bronze group mt-8 self-start">
            {t("cta_request")} <ArrowRight size={16} className="btn-arrow" />
          </motion.button>
        </div>
      </section>

      {/* STORY */}
      <section className="px-5 md:px-10 py-24 md:py-32">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="eyebrow" style={{ color: "#A96818" }}>{t("the_story")}</p>
            <p className="font-serif text-3xl md:text-4xl text-argali-obsidian leading-snug mt-4">{desc}</p>
            <p className="text-argali-brown/80 mt-6 leading-relaxed">
              {lang === "ru"
                ? "Мы подстраиваем ритм под вас и под сам Алтай: делаем остановки для фотографий, слушаем тишину и не спешим. Точный порядок точек может меняться в зависимости от погоды, дороги и сезона."
                : "We adapt the rhythm to you and to Altai itself: stops for photographs, room for silence, no rush. The exact order of points may change with weather, road and season."}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ROUTE STAGES */}
      {places.length > 0 && (
        <section className="px-5 md:px-10 pb-24" style={{ background: "#11110F" }}>
          <div className="max-w-[1600px] mx-auto pt-24">
            <Reveal><p className="eyebrow">{t("route_stages")}</p></Reveal>
            <div className="mt-10 space-y-px">
              {places.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.04}>
                  <div className="group grid grid-cols-1 md:grid-cols-[auto_1fr_2fr] gap-6 items-center py-6 border-t border-argali-brown/30">
                    <span className="font-serif text-4xl text-argali-bronze/70">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="font-serif text-2xl text-argali-ivory">{lang === "ru" ? p.name_ru : p.name_en}</h3>
                      <p className="text-[0.66rem] uppercase tracking-[0.14em] text-argali-sandstone/60 mt-1 flex items-center gap-1.5"><MapPin size={12} /> {distLabel(p.district, lang)}</p>
                    </div>
                    <div className="relative aspect-[16/7] overflow-hidden">
                      <img src={photoUrl(p.photo_id)} alt={p.name_ru} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MINI MAP */}
      {places.length > 0 && (
        <section className="px-5 md:px-10 py-24">
          <div className="max-w-[1600px] mx-auto">
            <Reveal><p className="eyebrow mb-8" style={{ color: "#A96818" }}>{lang === "ru" ? "Карта маршрута" : "Route map"}</p></Reveal>
            <div className="border border-argali-brown/20">
              <AltaiMap features={geoFeatures} routeCoords={routeCoords.length > 1 ? routeCoords : null} height="55vh" />
            </div>
            <p className="text-xs text-argali-brown/50 mt-3">{lang === "ru" ? "Точки показаны для ознакомления и не являются навигационными координатами." : "Points are shown for discovery and are not navigation coordinates."}</p>
          </div>
        </section>
      )}

      {/* INFO + LEGAL + PRICE */}
      <section className="px-5 md:px-10 pb-32">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <p className="eyebrow mb-6" style={{ color: "#A96818" }}>{t("info")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px">
              {info.map((it) => (
                <div key={it.label} className="flex gap-4 py-5 border-t border-argali-brown/20">
                  <it.icon size={20} className="text-argali-bronze shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50">{it.label}</p>
                    <p className="text-argali-obsidian mt-1">{it.value || "—"}</p>
                  </div>
                </div>
              ))}
              {e.children_note && (
                <div className="flex gap-4 py-5 border-t border-argali-brown/20 sm:col-span-2">
                  <Baby size={20} className="text-argali-bronze shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50">{t("children")}</p>
                    <p className="text-argali-obsidian mt-1">{e.children_note}</p>
                  </div>
                </div>
              )}
            </div>

            <p className="eyebrow mb-6 mt-14" style={{ color: "#A96818" }}>{t("legal_safety")}</p>
            <LegalBadges legal={e.legal} />
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 border border-argali-brown/20 p-8" style={{ background: "#FAFAF7" }}>
              <p className="text-[0.62rem] uppercase tracking-[0.16em] text-argali-brown/50">{t("price")}</p>
              <p className="font-serif text-3xl text-argali-obsidian mt-2">{priceLabel(e, lang)}</p>
              <div className="hair-line my-6" />
              <p className="text-sm text-argali-brown/70 leading-relaxed mb-6">
                {lang === "ru" ? "Оставьте запрос — уточним детали, даты и подготовим предложение под вашу группу." : "Send a request — we'll confirm details, dates and prepare an offer for your group."}
              </p>
              <button onClick={() => openBooking(e)} data-testid="detail-request-side" className="btn-bronze w-full justify-center group">
                {t("cta_request")} <ArrowRight size={16} className="btn-arrow" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
